import { useRef, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { AiPromptModal } from '@/components/commons/ai-prompt-modal'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Container } from '@/components/ui/container'

import { Settings01Icon, Stars01Icon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { ExpShellMerchOutputForm } from '@/features/experience-vetting/components/exp-shell-merch-output-form'
import { ExpVettingMerchOutputForm } from '@/features/experience-vetting/components/exp-vetting-merch-output-form'
import type {
	ExperienceShellMerchOutputSchema,
	ExperienceVettingMerchOutputSchema
} from '@/features/experience-vetting/form-schemas'
import { OpsVettingHero } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useMerchCopyAi } from '@/features/ops-vetting/hooks/use-merch-copy-ai'
import { EXPERIENCE_VETTING_PROMPT } from '@/features/ops-vetting/prompts/experience-vetting-prompt'
import { useTranslation } from '@/translations/i18n'

export const ExpVettingCopyTraining = () => {
	const { t } = useTranslation()
	const {
		aiResponse,
		isLoading: isAiLoading,
		onMerchCopyAiCall,
		resetAiResponse
	} = useMerchCopyAi<ExperienceVettingMerchOutputSchema>()
	const finalSubmitRef = useRef<HTMLButtonElement>(null)
	const aiSubmissionRef = useRef<HTMLButtonElement>(null)
	const [currentPrompt, setCurrentPrompt] = useState<string>(
		EXPERIENCE_VETTING_PROMPT
	)

	const handleAiSubmission: SubmitHandler<
		ExperienceShellMerchOutputSchema
	> = (data) => {
		resetAiResponse()
		onMerchCopyAiCall(data, currentPrompt)
	}

	const handleFinalSubmission: SubmitHandler<
		ExperienceVettingMerchOutputSchema
	> = (data) => {
		console.log('handleFinalSubmission', data)
	}

	return (
		<div className='grid h-full grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)] overflow-hidden'>
			<ScrollArea scrollbar={<Scrollbar />}>
				<Container
					className='grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]'
					fullWidth
				>
					<OpsVettingHero
						align='center'
						fullWidth
						justify='space-between'
					>
						<div className='flex flex-col gap-2'>
							<h3>Exp vetting Copy Training</h3>
							<p>
								Activities can be added and deleted only into
								the training tool just as a sample.
							</p>
						</div>
						<div className='flex gap-2'>
							<AiPromptModal
								currentPrompt={currentPrompt}
								isLoading={isAiLoading}
								onPromptChange={(newPrompt) =>
									setCurrentPrompt(newPrompt)
								}
								promptType='exp_vetting'
								trigger={
									<ButtonLegacy
										disabled={isAiLoading}
										type='button'
										variant='tertiary'
									>
										<Settings01Icon />
										{t(
											'vetting:merch.commons.customize_prompt'
										)}
									</ButtonLegacy>
								}
							/>
							<ButtonLegacy
								disabled={isAiLoading}
								onClick={() => aiSubmissionRef.current?.click()}
								type='button'
								variant='secondary'
							>
								<Stars01Icon />
								{t('vetting:merch.commons.improve_with_ai')}
							</ButtonLegacy>
						</div>
					</OpsVettingHero>

					<div className='mt-6 mb-10 flex w-full overflow-hidden rounded-2xl border border-neutral-200'>
						<ExpShellMerchOutputForm
							defaultValues={undefined}
							finalSubmitRef={aiSubmissionRef}
							handleFinalSubmissionCb={handleAiSubmission}
							isHostEdits={false}
							isLoading={false}
							isOriginalContent={true}
							isReadOnly={false}
							isTraining={true}
						/>
						<ExpVettingMerchOutputForm
							defaultValues={aiResponse}
							finalSubmitRef={finalSubmitRef}
							handleFinalSubmissionCb={handleFinalSubmission}
							isHostEdits={true}
							isLoading={isAiLoading}
							isReadOnly={false}
							isTraining={true}
						/>
					</div>
				</Container>
			</ScrollArea>
		</div>
	)
}

ExpVettingCopyTraining.displayName = 'ExpVettingCopyTraining'
